import { Component, EventEmitter, Input, OnChanges, Output, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize, Observable } from 'rxjs';
import { DeliveryCountrySource } from '../../services/amazon-finance.models';
import { AmazonFinanceService } from '../../services/amazon-finance.service';

// Order API paths follow Amazon's camelCase order model as stored in _syncAmazonOrders.
const ORDER_FACTS = [
  { label: 'DELIVERY_COUNTRY_ORDER_STATUS', report: 'order-status', api: 'order.orderStatus' },
  { label: 'DELIVERY_COUNTRY_PURCHASE_DATE', report: 'purchase-date', api: 'order.purchaseDate' },
  { label: 'DELIVERY_COUNTRY_CHANNEL', report: 'sales-channel', api: 'order.salesChannel' },
  { label: 'DELIVERY_COUNTRY_FULFILLMENT', report: 'fulfillment-channel', api: 'order.fulfillmentChannel' },
  { label: 'DELIVERY_COUNTRY_COUNTRY', report: 'ship-country', api: 'order.shippingAddress.countryCode' },
  { label: 'DELIVERY_COUNTRY_CITY', report: 'ship-city', api: 'order.shippingAddress.city' },
  { label: 'DELIVERY_COUNTRY_POSTAL_CODE', report: 'ship-postal-code', api: 'order.shippingAddress.postalCode' }
];

function sameVat(countryVat: number | undefined, vatImplied: number | null | undefined): boolean {
  return countryVat !== undefined && vatImplied !== null && vatImplied !== undefined && Math.abs(countryVat - vatImplied) < 0.05;
}

function display(data: Record<string, unknown> | null, path: string): string {
  const value = path.split('.').reduce<unknown>((node, key) => (node as Record<string, unknown> | null)?.[key], data);
  return value === null || value === undefined || value === '' ? '—' : String(value);
}

@Component({
  selector: 'gung-amazon-finance-delivery-country',
  templateUrl: './amazon-finance-delivery-country.component.html',
  styleUrl: './amazon-finance-delivery-country.component.scss',
  standalone: false
})
export class AmazonFinanceDeliveryCountryComponent implements OnChanges {
  private readonly financeService = inject(AmazonFinanceService);

  @Input({ required: true }) transactionId!: string;
  @Output() changed = new EventEmitter<void>();

  source: DeliveryCountrySource | null = null;
  countries: { code: string; name: string; vat?: number; matchesVat: boolean }[] = [];
  countryCode = '';
  reason = '';
  loading = false;
  working = false;
  error = '';

  ngOnChanges(): void {
    this.source = null;
    this.countryCode = '';
    this.reason = '';
    this.run(this.financeService.getDeliveryCountry(this.transactionId), false);
  }

  refetch(): void {
    this.run(this.financeService.refetchDeliveryCountry(this.transactionId), true);
  }

  save(): void {
    if (!this.countryCode) return;
    this.run(this.financeService.setDeliveryCountry(this.transactionId, this.countryCode, this.reason), true);
  }

  clear(): void {
    this.run(this.financeService.clearDeliveryCountry(this.transactionId), true);
  }

  // Usually only one source exists; the order API is preferred since the report is a snapshot from order time.
  orderFacts(): { label: string; value: string; highlight: boolean }[] {
    const useApi = !!this.source?.apiOrder;
    const data = (useApi ? this.source?.apiOrder : this.source?.reportOrder) || null;
    return ORDER_FACTS.map(fact => ({
      label: fact.label,
      value: display(data, useApi ? fact.api : fact.report),
      highlight: fact.label === 'DELIVERY_COUNTRY_COUNTRY'
    }));
  }

  matchingCountries(): string {
    return this.countries
      .filter(country => country.matchesVat)
      .map(country => country.name)
      .join(', ');
  }

  countryName(code: string): string {
    return this.source?.supportedCountries?.[code] || code;
  }

  choose(code: string): void {
    this.countryCode = code;
  }

  remapCounts(): { status: string; count: number }[] {
    return Object.entries(this.source?.remapResult?.mappingStatusCounts || {}).map(([status, count]) => ({
      status,
      count
    }));
  }

  private run(request: Observable<DeliveryCountrySource>, changesData: boolean): void {
    if (this.working) return;
    this.working = true;
    this.loading = !this.source;
    this.error = '';
    request
      .pipe(
        finalize(() => {
          this.working = false;
          this.loading = false;
        })
      )
      .subscribe({
        next: source => {
          this.source = source;
          const vat = source.countryVatPercent || {};
          this.countries = Object.entries(source.supportedCountries || {})
            .map(([code, name]) => ({ code, name, vat: vat[code], matchesVat: sameVat(vat[code], source.vatImplied) }))
            .sort((a, b) => a.name.localeCompare(b.name));
          if (changesData) {
            this.reason = '';
            this.changed.emit();
          }
        },
        error: (response: HttpErrorResponse) =>
          (this.error = response.error?.message || response.message || 'DELIVERY_COUNTRY_ERROR')
      });
  }
}
